/**
 * The screen's jolt (ADR 0049): a few whole pixels of camera offset that die
 * away, added where the camera centres on the player. Phaser's own shake
 * moves the camera by fractions of a pixel, which smears the pixel art; here
 * the offset is rounded, so the world moves in steps like everything else.
 */

export interface ShakeOffset {
  x: number;
  y: number;
}

/** How hard each kind of moment jolts the screen, in world pixels, and for how long. */
export const SHAKES = {
  /** The player's own critical hit. */
  crit: { pixels: 2, ms: 140 },
  /** A spell that strikes the ground around the caster. */
  slam: { pixels: 2, ms: 180 },
  /** The titan's wrath: the ground itself heaves. */
  quake: { pixels: 4, ms: 520 },
  /** A blow that takes a big bite out of the player's health. */
  heavy: { pixels: 3, ms: 220 },
  /** A new level. */
  level: { pixels: 1, ms: 400 },
  /** Shefira passes over (ADR 0067): the ground trembles, long and low. */
  rumble: { pixels: 1, ms: 900 },
  /** Shefira's feet strike the ground before the player. */
  landing: { pixels: 3, ms: 380 },
  /** Shefira roars at the breach. */
  roar: { pixels: 5, ms: 800 },
} as const;
export type ShakeKind = keyof typeof SHAKES;

export class ScreenShake {
  private pixels = 0;
  private startedAt = 0;
  private durationMs = 0;

  constructor(private readonly random: () => number = Math.random) {}

  /** A stronger jolt replaces a weaker one; a weaker one never cuts a strong one short. */
  kick(kind: ShakeKind, now: number): void {
    const { pixels, ms } = SHAKES[kind];
    if (this.strength(now) > pixels) return;
    this.pixels = pixels;
    this.startedAt = now;
    this.durationMs = ms;
  }

  /** The offset to add this frame: whole pixels, none once the jolt has died away. */
  offset(now: number): ShakeOffset {
    const strength = Math.round(this.strength(now));
    if (strength <= 0) return { x: 0, y: 0 };
    const axis = (): number => Math.round((this.random() * 2 - 1) * strength);
    return { x: axis(), y: axis() };
  }

  private strength(now: number): number {
    const elapsed = now - this.startedAt;
    if (this.durationMs <= 0 || elapsed >= this.durationMs) return 0;
    return this.pixels * (1 - elapsed / this.durationMs);
  }
}
